// src/pages/fleetChecksList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useCommands } from '../api/commands';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';

// Small util
const fmtDate = (s) => {
  if (!s) return '—';
  // accept ISO or dd/mm/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  try {
    const d = new Date(s);
    return d.toLocaleDateString('en-GB');
  } catch { return String(s); }
};
const truncate = (s, n=120) => (s && s.length > n ? s.slice(0, n - 1) + '…' : (s || '—'));

export default function FleetChecksList(){
  const { api } = useAuth();
  const cmd = useCommands();

  // data + load state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // filters
  const [q, setQ] = useState('');                 // search (plate, notes, user)
  const [from, setFrom] = useState('');           // yyyy-mm-dd
  const [to, setTo] = useState('');               // yyyy-mm-dd
  const [hasPhotos, setHasPhotos] = useState('all'); // 'all' | 'yes' | 'no'
  const [minMileage, setMinMileage] = useState('');
  const [maxMileage, setMaxMileage] = useState('');

  // pagination
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // expanded rows (show full payload & photos)
  const [open, setOpen] = useState({}); // {id: true}

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        // unified endpoint
        const list = await cmd.fleet.checks.list();
        if (!alive) return;
        // basic sanity: ensure array
        const arr = Array.isArray(list) ? list : [];
        // normalize a few common props for rendering
        const norm = arr.map(e => ({
          ...e,
          // prefer explicit date; fall back to ts
          _date: e.date || e.ts || null,
          _odometer: (e.odometer != null) ? Number(e.odometer) : null,
          _photos: Array.isArray(e.photos) ? e.photos : [],
          _by: e.submittedBy || e.user || '—',
        }));
        setRows(norm);
      } catch (e) {
        setError(e?.response?.data?.error || e?.message || 'Failed to load checks');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api, cmd]);

  // derive filtered rows
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();

    const fromMs = from ? new Date(from + 'T00:00:00Z').getTime() : null;
    const toMs   = to   ? new Date(to   + 'T23:59:59Z').getTime() : null;

    const minM = minMileage === '' ? null : Number(minMileage);
    const maxM = maxMileage === '' ? null : Number(maxMileage);

    return rows.filter(r => {
      // search text
      if (ql) {
        const hay = [
          r.reg, r._by, r.notes, JSON.stringify(r.items || {})
        ].join(' ').toLowerCase();
        if (!hay.includes(ql)) return false;
      }

      // date range (use _date or ts)
      if (fromMs || toMs) {
        const used = r._date || r.ts;
        const t = used ? new Date(used).getTime() : null;
        if (fromMs && (t == null || t < fromMs)) return false;
        if (toMs   && (t == null || t > toMs))    return false;
      }

      // photos
      if (hasPhotos !== 'all') {
        const present = (r._photos?.length || 0) > 0;
        if (hasPhotos === 'yes' && !present) return false;
        if (hasPhotos === 'no'  && present)  return false;
      }

      // mileage bounds
      if (minM != null && (r._odometer == null || r._odometer < minM)) return false;
      if (maxM != null && (r._odometer == null || r._odometer > maxM)) return false;

      return true;
    }).sort((a,b) => {
      // newest first, fall back to plate
      const ta = a._date ? new Date(a._date).getTime() : 0;
      const tb = b._date ? new Date(b._date).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return String(a.reg || '').localeCompare(String(b.reg || ''));
    });
  }, [rows, q, from, to, hasPhotos, minMileage, maxMileage]);

  // pager
  useEffect(() => { setPage(1); }, [q, from, to, hasPhotos, minMileage, maxMileage]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page-1)*pageSize, page*pageSize);

  // export helpers
  function exportJSON(){
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'vehicle-checks.json'; a.click();
    URL.revokeObjectURL(url);
  }
  function exportCSV(){
    const cols = ['date','reg','odometer','submittedBy','notes','photos'];
    const lines = [
      cols.join(',')
    ];
    for (const r of filtered){
      const row = [
        fmtDate(r._date || r.ts),
        (r.reg || '').replace(/,/g,' '),
        (r._odometer ?? '').toString(),
        (r._by || '').replace(/,/g,' '),
        (r.notes || '').replace(/[\r\n,]+/g,' ').slice(0,200),
        String(r._photos?.length || 0)
      ];
      lines.push(row.map(v => `"${String(v).replace(/"/g,'""')}"`).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'vehicle-checks.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="container">
      <PageHeader
        eyebrow="Fleet"
        title="Review checks"
        actions={
          <>
            <button className="btn secondary" onClick={exportCSV}><Icon name="download" size={15} /> Export CSV</button>
            <button className="btn secondary" onClick={exportJSON}><Icon name="download" size={15} /> Export JSON</button>
          </>
        }
      />

      {error && <div className="page-alerts"><div className="alert error">{error}</div></div>}

      {/* Filters */}
      <div className="card mb-16">
        <div className="filters-grid">
          <label className="input-label">Search
            <div className="search-wrap">
              <input
                className="input"
                placeholder="Plate, notes, user"
                value={q}
                onChange={(e)=>setQ(e.target.value)}
              />
              {q && (
                <button type="button" className="clear-x" aria-label="Clear search" onClick={()=>setQ('')}>
                  <Icon name="x" size={12} strokeWidth={2.5} />
                </button>
              )}
            </div>
          </label>

          <label className="input-label">From
            <input
              className="input"
              type="date"
              value={from}
              onChange={(e)=>setFrom(e.target.value)}
            />
          </label>

          <label className="input-label">To
            <input
              className="input"
              type="date"
              value={to}
              onChange={(e)=>setTo(e.target.value)}
            />
          </label>

          <label className="input-label">Photos
            <select
              className="input"
              value={hasPhotos}
              onChange={(e)=>setHasPhotos(e.target.value)}
            >
              <option value="all">With or without photos</option>
              <option value="yes">With photos</option>
              <option value="no">No photos</option>
            </select>
          </label>

          <label className="input-label">Min mileage
            <input
              className="input"
              type="number"
              placeholder="Any"
              value={minMileage}
              onChange={(e)=>setMinMileage(e.target.value)}
            />
          </label>

          <label className="input-label">Max mileage
            <input
              className="input"
              type="number"
              placeholder="Any"
              value={maxMileage}
              onChange={(e)=>setMaxMileage(e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="empty">Loading</div>
        ) : filtered.length === 0 ? (
          <div className="empty">No checks match your filters.</div>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Plate</th>
                    <th className="num">Mileage</th>
                    <th>Submitted by</th>
                    <th>Notes</th>
                    <th>Photos</th>
                    <th className="actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => {
                    const isOpen = !!open[r.id];
                    return (
                      <React.Fragment key={r.id || r.ts}>
                        <tr>
                          <td className="nowrap">{fmtDate(r._date || r.ts)}</td>
                          <td className="cell-strong nowrap">{r.reg || '—'}</td>
                          <td className="num">{(r._odometer ?? '—').toLocaleString?.() || (r._odometer ?? '—')}</td>
                          <td>{r._by}</td>
                          <td className="cell-muted" style={{ maxWidth: 380 }}>{truncate(r.notes, 120)}</td>
                          <td>
                            {r._photos?.length
                              ? <span className="badge info">{r._photos.length} photo{r._photos.length>1?'s':''}</span>
                              : <span className="cell-muted">None</span>}
                          </td>
                          <td className="actions">
                            <button
                              className="btn small secondary"
                              aria-expanded={isOpen}
                              onClick={() => setOpen(o => ({ ...o, [r.id]: !o[r.id] }))}
                            >
                              {isOpen ? 'Hide' : 'Details'}
                            </button>
                          </td>
                        </tr>

                        {isOpen && (
                          <tr className="detail-row">
                            <td colSpan={7}>
                              <div className="detail-panel">
                                <div className="detail-grid">
                                  <div>
                                    <div className="section-title">Form fields</div>
                                    {(!r.items || Object.keys(r.items).length === 0) ? (
                                      <div className="hint">No additional fields captured.</div>
                                    ) : (
                                      <dl className="kv">
                                        {Object.entries(r.items || {}).map(([k,v]) => (
                                          <React.Fragment key={k}>
                                            <dt>{k}</dt>
                                            <dd>{String(v ?? '—')}</dd>
                                          </React.Fragment>
                                        ))}
                                      </dl>
                                    )}
                                  </div>

                                  <div>
                                    <div className="section-title">Photos</div>
                                    {r._photos?.length ? (
                                      <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                                        {r._photos.map((p,i) => (
                                          <a
                                            key={p.url || i}
                                            href={cmd.fleet.uploads.url(p.url)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="badge info"
                                          >
                                            {p.name || `photo-${i+1}`}
                                          </a>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="hint">No photos attached.</div>
                                    )}
                                  </div>
                                </div>

                                {r.notes && (
                                  <div className="mt-16">
                                    <div className="section-title">Notes</div>
                                    <div>{r.notes}</div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pager">
              <span className="pager-info">
                Page {page} of {totalPages}, showing {pageRows.length} of {filtered.length}
              </span>
              <div className="pager-buttons">
                <button className="btn secondary" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Previous</button>
                <button className="btn secondary" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
